'use client';

import { Arrow } from '@radix-ui/react-tooltip';
import { Cell, ColumnDef, Row, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { VariantProps, cva } from 'class-variance-authority';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { InfoAlert } from '../Shared/InfoAlert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';
const dataTableVariants = cva('', {
  variants: {
    variant: {
      default: '',
      transparent: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
interface DataTableProps<TData extends {
  id?: string;
}, TValue> extends VariantProps<typeof dataTableVariants> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}
function getRowId<TData extends Partial<{
  id?: string;
  uniqueId: string;
}>>(row: TData, relativeIndex: number, parent: any) {
  if (row.id) {
    return row.id;
  }
  // In row object you have access to data.
  // You can choose parameter. In this example I used uniqueId
  const id = parent ? [parent.id, row.uniqueId].join('.') : row.uniqueId;
  return id ?? relativeIndex.toString();
}
interface DataTableCellTooltipProps<TData> {
  row: Row<TData>;
  cell: Cell<TData, unknown>;
}
function DataTableCellTooltip<TData>({
  row,
  cell
}: DataTableCellTooltipProps<TData>) {
  const cellValue = flexRender(cell.column.columnDef.cell, cell.getContext());
  return <TooltipProvider>
      <Tooltip disableHoverableContent delayDuration={0}>
        <TooltipTrigger asChild>
          <button className="p-4 text-left h-full w-full cursor-default overflow-hidden text-ellipsis whitespace-nowrap max-w-[400px]">
            {cellValue}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[400px] max-h[var(--radix-tooltip-content-available-height)]" align="start" alignOffset={5}>
          <span className="inline-flex items-center whitespace-pre-line line-clamp-[20]">
            <div className="flex flex-col space-y-1">
              {cell.column.columnDef.header && <span className="font-heading font-medium text-xs">
                  {cell.column.columnDef.header.toString()}
                </span>}
              <span className="text-xs text-balance [overflow-wrap:anywhere]">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </span>
            </div>
          </span>
          <Arrow style={{
          fill: 'rgb(0, 30, 113)'
        }} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>;
}
export function DataTable<TData extends Partial<{
  id?: string;
  uniqueId: string;
}>, TValue>({
  columns,
  data,
  variant,
  enableTooltips = true,
  noResults
}: Readonly<DataTableProps<TData, TValue>> & {
  enableTooltips?: boolean;
  noResults?: {
    title: string;
    description?: string;
  };
}) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId
  });
  return <Table variant={variant}>
      <TableHeader variant={variant}>
        {table.getHeaderGroups().map(headerGroup => <TableRow variant={variant} key={headerGroup.id}>
            {headerGroup.headers.map(header => {
          return <TableHead variant={variant} className={`w-[${header.getSize()}px]`} key={header.id}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>;
        })}
          </TableRow>)}
      </TableHeader>

      <TableBody variant={variant}>
        {table.getRowModel().rows?.length ? table.getRowModel().rows.map(row => <TableRow variant={variant} key={row.id} data-state={row.getIsSelected() && 'selected'}>
                {row.getVisibleCells().map(cell => <TableCell variant={variant} style={{
          minWidth: cell.column.getSize()
        }} key={cell.id}>
                    {enableTooltips && cell.column.id !== 'histogram' ? <DataTableCellTooltip row={row} cell={cell} /> : <div className="p-4 h-full w-full">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>}
                  </TableCell>)}
              </TableRow>) : noResults && <TableRow variant={variant}>
                <TableCell className="[&_div]:max-w-full [&_div]:whitespace-normal px-5 py-5" variant={variant} colSpan={columns.length}>
                  <InfoAlert title={noResults.title} description={noResults.description} />
                </TableCell>
              </TableRow>}
      </TableBody>
    </Table>;
}